import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { renderRichText } from '../../utils/richText';

/**
 * Search (Jenn, 2026-10-11 To-Do "Add search"; Oct 29: "Where is the search key in Reset? We need one.").
 * Opened from the Search button at the top of every page.
 *  - Members: every member-facing thing (GET /api/search).
 *  - Team365: everything, behind the scenes too (GET /api/admin/search), with a switch to see only
 *    what members see.
 * Each result: the bolded title of what it is in, the description underneath, and a link to it.
 * Topic tags count, so a topic finds every video and nudge about it even when the word isn't in the title.
 *
 * Oct 29 (Jenn, after trying it): a result has to open the THING, not just the page it lives on. Tapping a
 * result opens it right here: read the daily email or announcement, watch the replay or video, start the
 * lesson. Team365 also gets Copy buttons for IDs and links (to add to courses, or set up a member's sends).
 */
export default function SearchOverlay({ isAdmin, onClose }) {
  const [q, setQ] = useState('');
  const [everything, setEverything] = useState(!!isAdmin);
  const [state, setState] = useState({ results: null, loading: false, error: null, for: '' });
  const [open, setOpen] = useState(null); // the result being viewed
  const [copied, setCopied] = useState(null);
  const copy = async (label, value) => {
    try { await navigator.clipboard.writeText(value); setCopied(label); setTimeout(() => setCopied(c => (c === label ? null : c)), 1500); }
    catch { window.prompt(`Copy the ${label}:`, value); }
  };
  const inputRef = useRef(null);
  const openRef = useRef(null);
  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (openRef.current) setOpen(null); else onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const run = async (e) => {
    e?.preventDefault();
    const term = q.trim();
    if (term.length < 2) { setState({ results: null, loading: false, error: 'Type at least 2 letters.', for: '' }); return; }
    setOpen(null);
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(isAdmin && everything ? '/admin/search' : '/search', { params: { q: term } });
      setState({ results: data.results, loading: false, error: null, for: term });
    } catch (err) {
      setState({ results: null, loading: false, error: err.response?.data?.error || 'Search did not work just now. Try again.', for: '' });
    }
  };

  // Results grouped by what they are, in the order they came.
  const groups = [];
  for (const r of state.results || []) {
    let g = groups.find(x => x.kind === r.kind);
    if (!g) { g = { kind: r.kind, items: [] }; groups.push(g); }
    g.items.push(r);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-16 md:pt-20" role="dialog" aria-label="Search">
      <button className="absolute inset-0 bg-black/30" onClick={onClose} aria-label="Close search" />
      <div className="relative w-full max-w-2xl max-h-[80vh] flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden">
        <form onSubmit={run} className="p-4 border-b border-gray-100">
          <div className="flex gap-2">
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} type="search"
              placeholder={isAdmin && everything ? 'Search everything in 365reset…' : 'Search courses, lessons, events, replays, announcements, My Hive…'}
              className="flex-1 min-w-0 rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-reset-brown focus:border-reset-terracotta focus:outline-none" />
            <button type="submit" className="rounded-xl bg-reset-terracotta text-white text-sm font-semibold px-4">Search</button>
          </div>
          <div className="flex items-center justify-between mt-2">
            {isAdmin ? (
              <label className="flex items-center gap-2 text-xs text-gray-500">
                <input type="checkbox" checked={!everything} onChange={e => setEverything(!e.target.checked)} />
                Only what members see
              </label>
            ) : <span />}
            <button type="button" onClick={onClose} className="text-xs text-gray-400 hover:text-reset-brown">Close</button>
          </div>
        </form>
        {open && (
          <div className="overflow-y-auto p-4">
            <button type="button" onClick={() => setOpen(null)} className="text-xs text-gray-500 hover:text-reset-brown mb-3">← Back to results</button>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{open.kind}</p>
            <p className="text-base font-bold text-reset-brown mt-0.5">{open.title}</p>
            {open.open?.when && <p className="text-xs text-gray-500 mt-0.5">{open.open.when}</p>}
            {(open.action || open.link) && (
              <div className="flex flex-wrap gap-2 mt-3">
                {open.action && (
                  <Link to={open.action.to} onClick={onClose} className="rounded-xl bg-reset-terracotta text-white text-sm font-semibold px-4 py-2 hover:opacity-90">{open.action.label}</Link>
                )}
                {open.link && open.link !== open.action?.to && (
                  <Link to={open.link} onClick={onClose} className="rounded-xl border border-gray-200 text-reset-brown text-sm font-medium px-4 py-2 hover:border-reset-terracotta">Go to where it lives</Link>
                )}
              </div>
            )}
            {open.ids?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {open.ids.map(x => (
                  <button key={x.label} type="button" onClick={() => copy(x.label, x.value)} title={x.value}
                    className="rounded-lg border border-gray-200 text-xs text-reset-brown px-3 py-1.5 hover:border-reset-terracotta">
                    {copied === x.label ? 'Copied ✓' : `Copy ${x.label}`}{x.label === 'Day' ? ` (${x.value})` : ''}
                  </button>
                ))}
              </div>
            )}
            {(open.open?.videos || []).filter(Boolean).map((src, i) => (
              <div key={src} className="rounded-xl overflow-hidden bg-black aspect-video mt-4">
                <iframe src={src} title={`${open.title}${i ? ` (part ${i + 1})` : ''}`} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
              </div>
            ))}
            {open.open?.image && <img src={open.open.image} alt={open.open.imageAlt || ''} className="mt-4 w-full max-h-80 object-contain rounded-xl bg-reset-creamDark" />}
            {open.open?.text && <div className="mt-4 text-sm text-gray-700 leading-relaxed">{renderRichText(open.open.text)}</div>}
            {!open.open && !open.action && <p className="mt-4 text-sm text-gray-500">{open.detail}</p>}
          </div>
        )}
        <div className={`overflow-y-auto p-4 ${open ? 'hidden' : ''}`}>
          {state.loading && <p className="text-sm text-gray-400">Searching…</p>}
          {state.error && <p className="text-sm text-gray-600">{state.error}</p>}
          {state.results && !state.loading && state.results.length === 0 && (
            <p className="text-sm text-gray-500">Nothing found for "{state.for}". Try a shorter word.</p>
          )}
          {groups.map(g => (
            <div key={g.kind} className="mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">{g.kind}</p>
              <ul className="divide-y divide-gray-100">
                {g.items.map((r, i) => (
                  <li key={i}>
                    <button type="button" onClick={() => setOpen(r)} className="w-full text-left block py-2 group">
                      <span className="block text-sm font-semibold text-reset-brown group-hover:text-reset-terracotta">{r.title}</span>
                      {r.detail && <span className="block text-xs text-gray-600 mt-0.5 leading-relaxed">{r.detail}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
